import { useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Handshake, HeartHandshake, Share2 } from "lucide-react";

export default function GetInvolved() {
  const { t } = useLang();
  const TYPES = [
    { value: "partner", label: t("gi.partner"), desc: t("gi.partnerDesc"), Icon: Handshake },
    { value: "support", label: t("gi.support"), desc: t("gi.supportDesc"), Icon: HeartHandshake },
    { value: "other", label: t("gi.share"), desc: t("gi.shareDesc"), Icon: Share2 },
  ];
  const [form, setForm] = useState({ name: "", email: "", type: "partner", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/messages", form);
      setDone(true);
      toast.success(t("gi.thanks"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="eyebrow mb-6">{t("gi.eyebrow")}</div>
      <h1 className="font-headline text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight">
        {t("gi.title")}
      </h1>
      <p className="font-body text-lg md:text-xl text-[#3A3A3A] mt-8 max-w-3xl leading-relaxed">
        {t("gi.intro")}
      </p>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#DCDCDC]">
        {TYPES.map((t) => (
          <div key={t.value} className="border-r border-b border-[#DCDCDC] p-8">
            <t.Icon size={28} strokeWidth={1.25} />
            <div className="font-headline text-2xl mt-6">{t.label}</div>
            <p className="font-body text-[#3A3A3A] mt-3">{t.desc}</p>
          </div>
        ))}
      </div>

      <hr className="hairline my-16" />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="eyebrow">Write to Us</div>
          <p className="font-body text-[#3A3A3A] mt-4">
            Send a note. We read every message and reply within a few days.
          </p>
        </div>
        <div className="md:col-span-8">
          {done ? (
            <div className="border border-[#111111] p-10 text-center" data-testid="contact-success">
              <div className="font-headline text-3xl">Thank you.</div>
              <p className="font-body text-[#3A3A3A] mt-3">Your message is with us. We'll be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
              <label className="block">
                <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">Name</span>
                <input required className="editorial-input" data-testid="msg-name" value={form.name} onChange={(e) => onChange("name", e.target.value)} />
              </label>
              <label className="block">
                <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">Email</span>
                <input required type="email" className="editorial-input" data-testid="msg-email" value={form.email} onChange={(e) => onChange("email", e.target.value)} />
              </label>
              <label className="block md:col-span-2">
                <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">I would like to…</span>
                <select className="editorial-input" data-testid="msg-type" value={form.type} onChange={(e) => onChange("type", e.target.value)}>
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] block mb-2">Message</span>
                <textarea required rows={6} className="editorial-input" data-testid="msg-message" value={form.message} onChange={(e) => onChange("message", e.target.value)} />
              </label>
              <div className="md:col-span-2">
                <button disabled={submitting} type="submit" className="btn-primary" data-testid="msg-submit">
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <hr className="hairline my-16" />

      <div className="border border-[#111111] p-8 md:p-12" data-testid="gi-research-forms">
        <div className="eyebrow mb-4">Public Research — Official Forms</div>
        <h2 className="font-headline text-3xl md:text-4xl leading-tight mb-6">Take part in ongoing studies.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-[#DCDCDC] p-6">
            <div className="font-headline text-xl">Vote16 Research Participant Survey</div>
            <p className="font-body text-[#3A3A3A] mt-2">Contribute to our study on youth voting age and civic participation.</p>
            <a href="https://form.typeform.com/to/qxhyzvus" target="_blank" rel="noopener noreferrer" className="btn-primary mt-5 inline-block" data-testid="gi-form-1">Take the Vote16 Survey</a>
          </div>
          <div className="border border-[#DCDCDC] p-6">
            <div className="font-headline text-xl">What Carries Us</div>
            <p className="font-body text-[#3A3A3A] mt-2">Share what sustains you, your community, and your work.</p>
            <a href="https://form.typeform.com/to/m9xk1zUt" target="_blank" rel="noopener noreferrer" className="btn-outline mt-5 inline-block" data-testid="gi-form-2">Open the What Carries Us Form</a>
          </div>
        </div>
      </div>
    </div>
  );
}
